import React, { memo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Button } from '@lt/components';

const MAX_LINES_NUMBER = 6;

interface ReviewShowMoreProps {
  content: string;
  overlayColor?: string;
  maxContentLength?: number;
  maxContainerHeight?: number;
  showExpandButton?: boolean;
}

const ReviewShowMore = ({
  content,
  overlayColor,
  maxContentLength = 550,
  maxContainerHeight = 70,
  showExpandButton = true,
}: ReviewShowMoreProps) => {
  const [isHidden, setIsHidden] = useState(true);
  const handleClick = useCallback(
    () => setIsHidden((prevValue) => !prevValue),
    [],
  );

  const formattedContent = content.trim();
  const lineNumbers = formattedContent.split('\n').length;

  const shouldClampText =
    formattedContent.length > maxContentLength ||
    lineNumbers > MAX_LINES_NUMBER;
  const isClamped = isHidden && shouldClampText;

  return (
    <>
      <StyledText
        isClamped={isClamped}
        maxContainerHeight={maxContainerHeight}
        overlayColor={overlayColor}
      >
        {formattedContent}
      </StyledText>
      {isClamped && showExpandButton && (
        <StyledButton onClick={handleClick} size="tiny">
          Показать полностью
        </StyledButton>
      )}
    </>
  );
};

const StyledText = styled.div<
  { isClamped: boolean } & Pick<
    ReviewShowMoreProps,
    'maxContainerHeight' | 'overlayColor'
  >
>`
  position: relative;
  white-space: pre-line;

  ${({ isClamped, maxContainerHeight, overlayColor, theme }) =>
    isClamped &&
    css`
      overflow: hidden;
      height: ${maxContainerHeight}px;

      &::before {
        position: absolute;
        z-index: 1;
        bottom: 0;
        display: block;
        width: 100%;
        height: 50px;
        pointer-events: none;
        content: '';
        background-image: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          ${overlayColor || theme.colors.white} 70%
        );
      }
    `}
`;

const StyledButton = styled(Button)`
  max-width: none;
  font-size: 14px;
`;

export default memo(ReviewShowMore);
